import { hash } from './hash'

/**
 * Fields that mean "the document changed".
 *
 * Deliberately excludes `thumbnail_url` and `preview_url` (derived, and the
 * editor writes them itself on every save) and `updated`/`created` (server
 * bookkeeping). `Mail.updated` in particular is `auto_now`, so it also bumps on
 * writes that never touch the body — unscheduling a newsletter, or a responder
 * queue picking up a new subject. Keying off content instead of the timestamp
 * is what keeps those from surfacing as phantom changes. See FE-1354.
 */
const CONTENT_FIELDS = ['subject', 'pre_header', 'language']

/**
 * The API returns `body` as a JSON string, but EditorModel.load() and amend()
 * both parse it in place on the object they are given, so the same document can
 * reach us as either shape.
 *
 * Canonicalise by round-tripping through the parser rather than comparing the
 * raw text. Comparing text would make a baseline taken from a parsed body
 * permanently unequal to the string the API returns whenever the two differ
 * only in whitespace or key order — every poll would then report a change and
 * reload on a loop.
 */
const normalizeBody = (body) => {
  if (body === undefined || body === null || body === '') {
    return ''
  }
  if (typeof body !== 'string') {
    return JSON.stringify(body)
  }
  try {
    return JSON.stringify(JSON.parse(body))
  } catch (e) {
    // Not JSON — compare it as the opaque string it is.
    return body
  }
}

/**
 * Stable fingerprint of the server's copy of a document, used to notice that
 * someone else saved while this tab had it open.
 *
 * @param {object} item - an item as returned by the API
 * @return {string}
 */
export const fingerprint = (item) => {
  if (!item) {
    return ''
  }

  return hash(
    CONTENT_FIELDS.reduce(
      (acc, field) => {
        acc[field] = item[field] === undefined ? null : item[field]
        return acc
      },
      { body: normalizeBody(item.body) }
    )
  )
}

/**
 * Whether the tab's own copy already holds the content of the server's copy.
 *
 * Compares only the fields the server returned. serialize() always includes
 * every content field, but not every endpoint returns them: the autoresponder
 * endpoint has no `pre_header` or `language` (BE-3229). A field the server
 * does not return cannot differ, and comparing it anyway would never match.
 *
 * @param {object} remote - an item as returned by the API
 * @param {object} local - the tab's EditorModel.serialize()
 * @return {boolean}
 */
export const matchesLocal = (remote, local) => {
  if (!remote || !local) {
    return false
  }

  const shaped = CONTENT_FIELDS.reduce(
    (acc, field) => {
      if (field in remote) {
        acc[field] = local[field]
      }
      return acc
    },
    { body: local.body }
  )

  return fingerprint(shaped) === fingerprint(remote)
}

/**
 * Whether the server's `updated` is later than the one this tab holds.
 *
 * Strictly later: the API sends whole seconds (`%Y-%m-%dT%H:%M:%SZ`), so a
 * save in the same second as this tab's last one looks equal and can't be told
 * apart. Treating "different" as newer instead would make a tab block its own
 * save whenever the two strings differ only in format. Unparseable values never
 * count as newer, so a missing or odd timestamp can't block saving. See FE-1441.
 *
 * @param {string} server - `updated` from the server's copy
 * @param {string} local - `updated` of the version this tab holds
 * @return {boolean}
 */
export const isNewerServerVersion = (server, local) => {
  const serverTime = Date.parse(server)
  const localTime = Date.parse(local)
  if (Number.isNaN(serverTime) || Number.isNaN(localTime)) {
    return false
  }
  return serverTime > localTime
}
